import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkItemsByProjectId } from "@/Redux/Thunks/projectManagementThunk";
import { useParams } from "react-router-dom";
import { fetchUsersData } from "@/Redux/Thunks/usersThunk";
import { fetchSystemList } from "@/Redux/Thunks/systemListsThunk";
import {
  selectIssuesData,
  selectIssuesLoading,
  selectIssuesError,
} from "@/Redux/Slices/issuesSlice";
import { selectUsersData } from "@/Redux/Slices/usersSlice";
import Typography from "@/Utility/Typography";
import IssueList from "@/Pages/Issues/IssueList";
import {
  ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS,
  buildProjectStatusOptionsByCategory,
  getListFromApiResponse,
  mapUserToAssigneeOptionForDropdown,
  mapSystemListItemToOption,
} from "@/Pages/Issues/issueMappers";
import { fetchProjectStatuses } from "@/Redux/Thunks/projectStatusesThunk";
import { selectProjectStatusesResponse } from "@/Redux/Slices/projectStatusesSlice";
import { ISSUES_LISTING_FILTER_DEFAULT, buildProjectIssuesListBody } from "./issues.mapper";
import {
  WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY,
  readStoredListPageSize,
} from "@/Utility/Pagination/listPaginationConstants";

const ProjectsIssues = ({
  projectSlug: projectSlugProp,
  listingFilter,
  setListingFilter,
  currentPage,
  setCurrentPage,
  onOpenWorkItemDetail,
}) => {
  const dispatch = useDispatch();
  const { projectSlug: projectSlugFromRoute } = useParams();
  const projectSlug = projectSlugProp ?? projectSlugFromRoute;

  const issuesData = useSelector(selectIssuesData);
  const loading = useSelector(selectIssuesLoading);
  const error = useSelector(selectIssuesError);
  const usersData = useSelector(selectUsersData);

  const [typeOptions, setTypeOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const statusesResponse = useSelector((state) =>
    projectSlug ? selectProjectStatusesResponse(state, projectSlug) : null
  );

  useEffect(() => {
    dispatch(fetchUsersData({ getAll: true }));
  }, [dispatch]);

  useEffect(() => {
    if (projectSlug) {
      dispatch(fetchProjectStatuses(projectSlug));
    }
  }, [dispatch, projectSlug]);

  const statusOptionsForInlineEdit = buildProjectStatusOptionsByCategory(
    statusesResponse?.data || []
  );

  useEffect(() => {
    dispatch(fetchSystemList("WORK_ITEM_TYPE"))
      .unwrap()
      .then((raw) => {
        const list = getListFromApiResponse(raw);
        const options = list.map((item) => mapSystemListItemToOption(item));
        setTypeOptions(options);
      })
      .catch(() => setTypeOptions([]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList("PRIORITY"))
      .unwrap()
      .then((raw) => {
        const list = getListFromApiResponse(raw);
        const options = list.map((item) => mapSystemListItemToOption(item));
        setPriorityOptions(options);
      })
      .catch(() => setPriorityOptions([]));
  }, [dispatch]);

  const handleFilterReset = () => {
    setListingFilter({ ...ISSUES_LISTING_FILTER_DEFAULT });
    setCurrentPage(1);
  };

  const handleIssueCreated = () => {
    setCurrentPage(1);
    if (projectSlug) {
      dispatch(
        fetchWorkItemsByProjectId({
          projectId: projectSlug,
          body: buildProjectIssuesListBody(listingFilter, {
            page: 1,
            limit: readStoredListPageSize(WORK_ITEMS_LIST_PAGE_SIZE_STORAGE_KEY),
          }),
        })
      );
    }
  };

  const list = issuesData?.list ?? [];
  const assigneeOptions = getListFromApiResponse(usersData).map(mapUserToAssigneeOptionForDropdown);

  const projectIssuesData = {
    ...(issuesData || {}),
    list,
  };

  return (
    <div>
      {error && !loading && (
        <div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-red-700">
          <Typography variant="text2">{error}</Typography>
        </div>
      )}
      <IssueList
        issuesData={projectIssuesData}
        loading={loading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        listingFilter={listingFilter}
        onCommitListingFilter={(filters) => setListingFilter(filters)}
        handleFilterReset={handleFilterReset}
        assigneeOptions={assigneeOptions}
        filterMode="all"
        projectName={null}
        projectSlug={projectSlug}
        onIssueCreated={handleIssueCreated}
        typeOptions={typeOptions}
        statusFilterOptions={ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS}
        statusOptions={statusOptionsForInlineEdit}
        priorityOptions={priorityOptions}
        onOpenWorkItemDetail={onOpenWorkItemDetail}
      />
    </div>
  );
};

export default ProjectsIssues;
